import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { apiUrl } from '../../../apiBase';
import { tutorialFinished, tutorialScreens, type PublicPart, type PublicTutorialData } from './PublicTutorial';
import { ClipPlayer } from '../ClipPlayer';
import type { Clip, ClipPart } from '../assemblyClip';
import { levelTurn } from '../assemblyStore';
import { publicMeshUrl } from '../publicUrl';
import { PublicScreen, ScreenProgress } from './PublicScreen';

// WHAT TO HAVE IN HAND BEFORE THE FIRST STEP: the tools, then the parts — two screens between
// the splash and the steps, each a list of cards with the part's drawing from the manual. A
// first version: no ticking off, no "I don't have this tool", no "identify my part".
//
// `at` and `of` are this screen's place among the screens of this kind ("01 / 02"): a manual
// with no tools has only the parts screen, and says "01 / 01".
// How a card sees its part: a part as the cover sees the product, a tool (and a long part, laid
// like one) from higher and closer.
// Zoomed past the fit (1.3): the fit keeps room round the part for the app's measurement labels,
// and on a card that room was white (Louis 2026-10-23).
const PART_SHOT = { turn: levelTurn(15, 40), zoom: 1.3 };
const TOOL_SHOT = { turn: levelTurn(35, 20), zoom: 1.1 };
// A PART'S LONGEST SIDE IN MILLIMETRES, as the steps draw it: the size Blender built it at, else
// the one the manual printed; 0 where neither is known.
function sizeMm(cp: ClipPart | undefined): number {
  if (!cp) return 0;
  if (cp.builtMm) return Math.max(cp.builtMm.x, cp.builtMm.y, cp.builtMm.z);
  const d = cp.dims;
  return d ? Math.max(0, d.x ?? 0, d.y ?? 0, d.z ?? 0, d.lengthMm ?? 0, d.bodyDiaMm ?? 0) : 0;
}

// A tool modelled standing, its length up the vertical, turned to lie along the floor.
const TOOL_LAID: [number, number, number] = [0, 90, 0];
// AND A LONG PART LIKEWISE, a screw or a dowel (Louis 2026-10-23): its length across the card
// rather than up it, where it was a thin stick in a wide frame. Long is the upright at least
// two and a half times the wider of the other two.
const standsLong = (cp: ClipPart | undefined): boolean => {
  const b = cp?.builtMm;
  return !!b && b.z >= 2.5 * Math.max(b.x, b.y);
};
// THE SMALLEST A PART IS DRAWN, against the biggest on its screen, which fills its card.
const SMALLEST_DRAWN = 0.5;

export function TutorialKit({ kind, parts, at, of, onNext, onBack, docId, data, brightness, variant }: {
  kind: 'tools' | 'parts';
  parts: PublicPart[];
  docId: string;
  data: PublicTutorialData;
  brightness: number;
  variant: string;
  at: number;
  of: number;
  onNext: () => void;
  onBack: () => void;
}) {
  const { t, language } = useLanguage();
  const tools = kind === 'tools';
  const name = (p: PublicPart) => (language === 'fr' ? p.nameFr || p.nameEn : p.nameEn || p.nameFr);
  // The parts screen's lead counts what is in the box, every copy of it: "10 items".
  const count = parts.reduce((n, p) => n + (p.qty ?? 1), 0);

  // EACH PART IN 3D (Louis 2026-10-23), alone, where the finished product's first part stands.
  // Sized as the steps size it where one of them draws it; a part none of them does (the allen
  // key, which no step picks up) at its model's own size, which alone on a card is all the same.
  // A TOOL LAID DOWN, as on a table and as the manual draws it, and seen from closer and higher:
  // stood up as modelled, a screwdriver was a thin stick in the middle of a wide card.
  const clipOf = useMemo(() => {
    const finished = tutorialFinished(tutorialScreens(data));
    const out = new Map<number, Clip>();
    if (!finished) return out;
    const drawn = new Map<number, ClipPart>(tutorialScreens(data)
      .flatMap((s) => s.sub.clip?.parts ?? []).map((cp) => [cp.job, cp]));
    // SMALL PARTS LOOK SMALL (Louis 2026-10-23): each card fits its own part to the frame, so a
    // washer filled its card as the backrest filled its own. Now the biggest on this screen fills
    // its card, the smallest half of its own (SMALLEST_DRAWN), and the rest stand between the two
    // by the LOGARITHM of their size: the order is kept, a screw is plainly smaller than a seat,
    // and nothing is a speck. Scaled by real size (the square root, then the cube root), the
    // washer came out a speck and then still too small. A part of no known size keeps the card.
    const sizes = parts.map((p) => (p.job == null ? 0 : sizeMm(drawn.get(p.job)))).filter((n) => n > 0);
    const largest = Math.max(0, ...sizes);
    const smallest = Math.min(largest, ...sizes);
    const span = Math.log(largest / smallest);
    for (const p of parts) {
      if (p.job == null || out.has(p.job)) continue;
      const cp = drawn.get(p.job);
      if (cp?.pieces?.length) continue;
      const own = sizeMm(cp);
      const scale = own > 0 && span > 0
        ? SMALLEST_DRAWN + ((1 - SMALLEST_DRAWN) * Math.log(own / smallest)) / span
        : 1;
      const laid = p.tool || standsLong(cp);
      const base = laid ? TOOL_SHOT : PART_SHOT;
      const shot = { turn: base.turn, zoom: base.zoom * scale };
      out.set(p.job, {
        ...finished,
        movement: {
          ...finished.movement, copies: [], mirrorAll: false,
          targetSeat: [0, 0, 0], targetSpin: laid ? [...TOOL_LAID] : [0, 0, 0],
          camStartRot: [...shot.turn], camEndRot: [...shot.turn],
          camStartZoom: shot.zoom, camEndZoom: shot.zoom,
        },
        parts: [{
          ...(cp ?? { job: p.job, trueScale: true, dims: null, builtMm: null }),
          label: p.nameFr || p.nameEn, instanceId: 'target', mirror: false,
        }],
      });
    }
    return out;
  }, [data, parts]);
  const meshUrl = useCallback((job: number) => publicMeshUrl(docId, job, variant), [docId, variant]);

  return (
    <PublicScreen
      header={<ScreenProgress at={at} of={of} onBack={onBack} />}
      primary={{ label: t('notice.public.next'), onClick: onNext }}
    >
      {/* The frame's full height, which the tools share out (below). */}
      <div className="flex h-full flex-col">
        <h1 className="text-center font-sora text-3xl font-bold text-notice-blue">
          {t(tools ? 'notice.public.toolsTitle' : 'notice.public.partsTitle')}
        </h1>
        <p className="mt-1 text-center text-sm text-notice-blue">
          {tools ? t('notice.public.toolsLead') : t('notice.public.partsLead').replace('{n}', String(count))}
        </p>

        {/* A tool a card the width of the screen, its name centred over it, the tools sharing the
            height there is so the screen does not scroll (Louis 2026-10-23); the parts two to a
            row, the name over the drawing and the count under it. */}
        <div className={`mt-5 gap-4 ${tools ? 'flex min-h-0 flex-1 flex-col' : 'grid grid-cols-2'}`}>
          {parts.map((p, i) => (
            <div
              key={i}
              className={`relative flex flex-col overflow-hidden rounded-xl border border-notice-blue px-3 pb-6 pt-2.5 ${tools ? 'min-h-28 flex-1' : 'h-40 items-center'}`}
            >
              <span className="pointer-events-none relative z-10 text-center text-sm font-bold text-notice-blue">
                {name(p)}
                {/* AND THE MANUAL'S LETTER, where it prints one (Louis 2026-10-28): the steps' sentences
                    say "(C)", and this is the screen the reader learns which part that is. */}
                {p.letter ? ` (${p.letter})` : ''}
                {/* In the box or to find: a tool the carton lacks is one to fetch before starting. */}
                {tools && p.supplied != null && (
                  ` (${t(p.supplied ? 'notice.public.supplied' : 'notice.public.notSupplied')})`
                )}
              </span>
              {p.job != null && clipOf.has(p.job) ? (
                <PartModel clip={clipOf.get(p.job)!} label={name(p)} meshUrl={meshUrl} brightness={brightness} inList={!tools} />
              ) : p.imageUrl && (
                <img
                  src={apiUrl(p.imageUrl)}
                  alt=""
                  className="mt-1 min-h-0 w-full flex-1 object-contain mix-blend-multiply"
                  draggable={false}
                />
              )}
              {!tools && p.qty != null && (
                <span className="pointer-events-none absolute bottom-1.5 right-2.5 z-10 text-xs tabular-nums text-notice-blue/70">×{p.qty}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </PublicScreen>
  );
}

// ONE PART'S MODEL ON ITS CARD. Drawn only while the card is on screen: a page of fifteen parts is
// fifteen 3D views, and a phone's browser keeps a handful of them alive at once. Each one turned
// by dragging it, as everywhere else on the page (Louis 2026-10-23); the list scrolls from the
// gaps between the cards.
// THE WHOLE CARD, under its name: inset, the picture's own edge cut the model off in a white band
// inside the card's border. And the shot starts BELOW the name, which it touched (Louis
// 2026-10-23): the name's height is kept clear at the top (see AssemblyOpts.headroom), measured
// off the card, since a long name wraps to two lines.
function PartModel({ clip, label, meshUrl, brightness, inList }: {
  // On the parts screen, which scrolls: a swipe up or down is the page's (see
  // AssemblyOpts.scrollsUpDown). The tools screen does not scroll, and keeps the whole drag.
  inList: boolean;
  clip: Clip;
  label: string;
  meshUrl: (job: number) => string;
  brightness: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  const [headroom, setHeadroom] = useState<number | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    // The name is the card's first child; 2px under it (6 left too wide a gap, Louis 2026-10-23).
    const name = el.parentElement?.firstElementChild as HTMLElement | null;
    const measure = () => setHeadroom(name && name !== el ? name.offsetTop + name.offsetHeight + 2 : 0);
    measure();
    // AND AGAIN WHENEVER THE NAME CHANGES HEIGHT (Louis 2026-10-28): measured once, it was the
    // height before the page's font arrived, one line, and the name wrapped to two over the part.
    const grows = typeof ResizeObserver === 'undefined' || !name || name === el ? null : new ResizeObserver(measure);
    if (grows && name) grows.observe(name);
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting), { rootMargin: '100px' });
    io.observe(el);
    return () => { io.disconnect(); grows?.disconnect(); };
  }, []);
  return (
    <div ref={box} className="absolute inset-0">
      {seen && headroom != null && (
        <ClipPlayer
          clip={clip}
          label={label}
          meshUrl={meshUrl}
          className="h-full w-full"
          emptyClassName="h-full w-full text-notice-blue/50"
          brightness={brightness}
          autoPlay={false}
          still
          noBigView
          floorShadow
          headroom={headroom}
          scrollsUpDown={inList}
        />
      )}
    </div>
  );
}
